import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { localISODate, todayISO } from '../utils/dates';
import HistoryButton from './HistoryButton';

import DateInput from './DateInput';
const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};
const day = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const LAST_ACCOUNT_KEY = 'ibigdo.expenses.paidFrom';

const blank = () => ({ date: todayISO(), head: '', paidFrom: '', amount: '', paidTo: '', reference: '', notes: '' });

/**
 * Accounts > Expenses: rent, electricity, wages and the like, paid on the spot from cash or bank, in one entry with no
 * vendor. The list shows the period's expenses and what went on each head; a mistake is undone with a reversing entry.
 */
function Expenses({ active, currency = 'INR', onChanged }) {
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [range, setRange] = useState(() => {
    const now = new Date();
    return [localISODate(new Date(now.getFullYear(), now.getMonth(), 1)), todayISO()];
  });
  const [data, setData] = useState(null);

  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency, minimumFractionDigits: 2 });
    return (v) => f.format(Number(v || 0));
  }, [currency]);

  const loadOptions = useCallback(() => {
    axios.get('/api/expenses/options').then((r) => {
      setOptions(r.data);
      let last = '';
      try { last = localStorage.getItem(LAST_ACCOUNT_KEY) || ''; } catch (e) { /* no storage */ }
      const accounts = r.data.accounts || [];
      const pick = accounts.find((a) => String(a.ledgerId) === last) || accounts.find((a) => a.kind === 'CASH') || accounts[0];
      setForm((f) => ({ ...f, paidFrom: f.paidFrom || (pick ? String(pick.ledgerId) : '') }));
    }).catch((error) => toast.error(errorMessage(error, 'Could not load the expense heads')));
  }, []);

  const load = useCallback(() => {
    axios.get('/api/expenses', { params: { from: range[0], to: range[1] } })
      .then((r) => setData(r.data))
      .catch((error) => toast.error(errorMessage(error, 'Could not load expenses')));
  }, [range]);

  useEffect(() => { if (active) loadOptions(); }, [active, loadOptions]);
  useEffect(() => { if (active) load(); }, [active, load]);

  if (!active) return null;

  const save = async (e) => {
    e.preventDefault();
    if (!form.head) { toast.warn('Choose what it was for'); return; }
    if (!form.paidFrom) { toast.warn('Choose the cash or bank account it was paid from'); return; }
    if (!(Number(form.amount) > 0)) { toast.warn('Enter the amount'); return; }
    const [kind, value] = form.head.split(':');
    setSaving(true);
    try {
      const { data: saved } = await axios.post('/api/expenses', {
        date: form.date,
        head: kind === 'code' ? value : null,
        ledgerId: kind === 'ledger' ? Number(value) : null,
        paidFromLedgerId: Number(form.paidFrom),
        amount: Number(form.amount),
        paidTo: form.paidTo,
        reference: form.reference,
        notes: form.notes
      });
      try { localStorage.setItem(LAST_ACCOUNT_KEY, form.paidFrom); } catch (err) { /* no storage */ }
      toast.success(`${saved.number}: ${money(saved.amount)} for ${saved.head}`);
      setForm((f) => ({ ...blank(), date: f.date, head: f.head, paidFrom: f.paidFrom }));
      load();
      loadOptions();
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not record the expense'));
    } finally {
      setSaving(false);
    }
  };

  const undo = async (x) => {
    const reason = window.prompt(`Undo ${x.number} (${money(x.amount)} for ${x.head})? A reversing entry is posted. Why? (optional)`);
    if (reason === null) return;
    try {
      await axios.post(`/api/expenses/${x.id}/undo`, { reason });
      toast.success('Expense undone');
      load();
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not undo it'));
    }
  };

  const heads = options?.heads || [];
  const accounts = options?.accounts || [];

  return (
    <div>
      <Card className="mb-3">
        <Card.Header className="py-2"><strong>Record an expense</strong> <span className="small text-muted">paid from cash or bank, no bill to enter</span></Card.Header>
        <Card.Body>
          {!options ? <Spinner animation="border" size="sm" /> : accounts.length === 0 ? (
            <p className="text-muted mb-0">Add a cash-in-hand or bank account first (Accounts &rsaquo; Cash-in-Hand or Bank Accounts).</p>
          ) : (
            <Form onSubmit={save}>
              <Row className="g-2">
                <Col md={2}>
                  <Form.Label className="small mb-1">Date</Form.Label>
                  <DateInput size="sm" value={form.date} max={todayISO()} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </Col>
                <Col md={3}>
                  <Form.Label className="small mb-1">For</Form.Label>
                  <Form.Select size="sm" value={form.head} onChange={(e) => setForm({ ...form, head: e.target.value })}>
                    <option value="">Choose</option>
                    <optgroup label="Usual expenses">
                      {heads.filter((h) => h.usual).map((h) => <option key={h.code} value={`code:${h.code}`}>{h.name}</option>)}
                    </optgroup>
                    {heads.some((h) => !h.usual) && (
                      <optgroup label="Your other expense accounts">
                        {heads.filter((h) => !h.usual).map((h) => <option key={h.ledgerId} value={`ledger:${h.ledgerId}`}>{h.name}</option>)}
                      </optgroup>
                    )}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-1">Amount</Form.Label>
                  <Form.Control size="sm" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                </Col>
                <Col md={3}>
                  <Form.Label className="small mb-1">Paid from</Form.Label>
                  <Form.Select size="sm" value={form.paidFrom} onChange={(e) => setForm({ ...form, paidFrom: e.target.value })}>
                    {accounts.map((a) => <option key={a.ledgerId} value={a.ledgerId}>{a.kind === 'CASH' ? 'Cash' : 'Bank'}: {a.name}</option>)}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-1">Paid to (optional)</Form.Label>
                  <Form.Control size="sm" value={form.paidTo} maxLength={150} onChange={(e) => setForm({ ...form, paidTo: e.target.value })} />
                </Col>
                <Col md={6}>
                  <Form.Control size="sm" value={form.notes} maxLength={500} placeholder="Note (optional), e.g. October rent"
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </Col>
                <Col md={4}>
                  <Form.Control size="sm" value={form.reference} maxLength={100} placeholder="Receipt / UTR no. (optional)"
                    onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                </Col>
                <Col md={2} className="d-grid">
                  <Button size="sm" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Record expense'}</Button>
                </Col>
              </Row>
              <div className="small text-muted mt-2">
                A GST bill from a registered supplier goes in Purchase &rsaquo; Purchase Invoices instead, so the GST can be claimed.
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <DateInput size="sm" value={range[0]} max={range[1]} style={{ width: 'auto' }} onChange={(e) => setRange([e.target.value, range[1]])} />
        <span className="small text-muted">to</span>
        <DateInput size="sm" value={range[1]} min={range[0]} style={{ width: 'auto' }} onChange={(e) => setRange([range[0], e.target.value])} />
        {data && <strong className="ms-md-3">Spent {money(data.total)}</strong>}
      </div>
      {data && data.byHead.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-2">
          {data.byHead.map((h) => <Badge key={h.head} bg="light" text="dark" className="border fw-normal">{h.head}: {money(h.amount)}</Badge>)}
        </div>
      )}
      <Table striped bordered hover responsive size="sm" className="small">
        <thead>
          <tr><th>Date</th><th>No.</th><th>For</th><th>Paid to</th><th>From</th><th className="text-end">Amount</th><th /></tr>
        </thead>
        <tbody>
          {data && data.expenses.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-3">No expenses in this period.</td></tr>}
          {data && data.expenses.map((x) => (
            <tr key={x.id} className={x.status === 'UNDONE' ? 'text-muted' : ''}>
              <td className="text-nowrap">{day(x.date)}</td>
              <td className="font-monospace text-nowrap">{x.number}</td>
              <td>
                {x.head}
                {x.notes && <div className="text-muted">{x.notes}</div>}
              </td>
              <td>{x.paidTo || '-'}{x.reference && <div className="text-muted">{x.reference}</div>}</td>
              <td>{x.paidFrom}</td>
              <td className="text-end text-nowrap">{x.status === 'UNDONE' ? <s>{money(x.amount)}</s> : money(x.amount)}</td>
              <td className="text-end">
                {x.status === 'UNDONE'
                  ? <Badge bg="secondary" title={x.undoReason || ''}>Undone</Badge>
                  : <Button size="sm" variant="link" className="p-0" onClick={() => undo(x)}>Undo</Button>}
                <HistoryButton documentType="Expense" documentId={x.id} label={`Expense ${x.number}`} className="ms-1" />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export default Expenses;
